import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import clx from "clsx";
import Icon from "../icon";
import Box from "../box";
import Spinner from "../spinner";
import { store, UIState } from "../../models/store";
import { recipe, buttonOverrides } from "./button.helper";
import { isDefaultAccent, getAccentHover } from "../../helpers/style";
import { themeVars } from "../../styles/themes.css";
import { fullWidth, fullWidthHeight } from "../shared/shared.css";
import type { UnknownRecord } from "type-fest";
import type { ButtonProps } from "./button.types";
import type { ThemeVariant } from "../../models/system.model";
import type { OverrideStyleManager } from "../../styles/override/override";
import * as styles from "./button.css";

function Button(props: ButtonProps) {
  const cleanupRef = useRef<() => void>(null);
  const [_overrideManager, set_overrideManager] = useState(() => null);

  const [_theme, set_theme] = useState(() => "light");

  const [_themeAccent, set_themeAccent] = useState(() => null);

  function getStoreState() {
    // This seems weird but it's a workaround for one minor bug from mitosis // If we have any variables in any function scope that has the same name as the store state, mitosis understands that it's the same variable
    // and will attempt to transform those unwanted/unrelated variables into the ones in the state.<variable>
    // So we need to name these values differently (e.g. _keyA: valueA) or inverse
    return {
      theme: store.getState().theme,
      themeAccent: store.getState().themeAccent,
      overrideStyleManager: store.getState().overrideStyleManager,
    };
  }

  function getVars() {
    const accent = _themeAccent;
    const isDefaultAppearance = isDefaultAccent(accent) && accent === "blue";

    // Only allow accent customization for 'primary' Intent
    const isPrimaryIntent = props.intent === "primary";
    return isDefaultAppearance || !isPrimaryIntent
      ? _overrideManager?.applyOverrides(buttonOverrides.name)
      : assignInlineVars({
          [styles.buttonBgVar]: themeVars.colors.accent,
          [styles.buttonTextColorVar]: themeVars.colors.accentText,
          [styles.buttonHoverBgVar]: getAccentHover(themeVars.colors.accent),
        });
  }

  useEffect(() => {
    const uiStore = getStoreState();
    set_theme(uiStore[0]);
    set_themeAccent(uiStore[1]);
    set_overrideManager(uiStore[2]);
    cleanupRef.current = store.subscribe((newState, prevState) => {
      set_theme(newState.theme);
      set_themeAccent(newState.themeAccent);
      set_overrideManager(newState.overrideStyleManager);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (typeof cleanupRef.current === "function") cleanupRef.current();
    };
  }, []);

  return (
    <Box
      as={props.as}
      boxRef={props.buttonRef}
      {...props.attributes}
      attributes={{
        onClick: (event) => props.onClick?.(event),
        onMouseEnter: (event) => props.onHoverStart?.(event),
        onMouseLeave: (event) => props.onHoverEnd?.(event),
        disabled: props.disabled,
        // style: state.getVars(),
        ...props.domAttributes,
      }}
      className={clx(
        styles.buttonSize[props.size],
        recipe({
          as: props.as,
          variant: props.variant,
          intent: props.intent ?? "primary",
          isDisabled: props.disabled || props.isLoading,
          theme: getStoreState().theme,
        }),
        props.fluidWidth ? fullWidth : null,
        props.fluid ? fullWidthHeight : null,
        props.className
      )}
    >
      <Spinner
        size={props.iconSize}
        attributes={{
          display:
            props.isLoading && props.spinnerPlacement === "start"
              ? "inline-block"
              : "none",
        }}
      />
      <Icon
        name={props.leftIcon}
        size={props.iconSize}
        attributes={{
          display:
            !!props.leftIcon && !props.isLoading ? "inline-block" : "none",
          marginRight: !props.children ? "$0" : "$2",
        }}
      />
      {!props.isLoading ? <>{props.children}</> : null}
      <Icon
        name={props.rightIcon}
        size={props.iconSize}
        attributes={{
          display:
            !!props.rightIcon && !props.isLoading ? "inline-block" : "none",
          marginLeft: !props.children ? "$0" : "$2",
        }}
      />
      <Spinner
        size={props.iconSize}
        attributes={{
          display:
            props.isLoading && props.spinnerPlacement === "end"
              ? "inline-block"
              : "none",
        }}
      />
    </Box>
  );
}

Button.defaultProps = {
  as: "button",
  size: "md",
  intent: "primary",
  variant: "solid",
  spinnerPlacement: "start",
};

export default Button;
